import { AccountingEventsGroup, CanonicalBusinessEvent } from "../types";
import {
  DigitalBusinessDocument,
  DigitalCostDocument,
} from "../types/documents";

type JsonObject =
  | AccountingEventsGroup
  | DigitalBusinessDocument
  | DigitalCostDocument
  | CanonicalBusinessEvent
  | Record<string, unknown>;

/**
 * Safely parses a JSON object with proper typing
 * @param obj Object to parse
 * @returns Parsed object with proper typing
 */
function safeJsonParse<T>(obj: T): JsonObject {
  return JSON.parse(JSON.stringify(obj)) as JsonObject;
}

/**
 * Removes a single key from a JSON object
 * @param object The object to remove the key from
 * @param toRemove Key to remove
 * @returns A new object with the specified key removed
 */
export function removeKeyFromJson(
  object:
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | CanonicalBusinessEvent,
  toRemove: string
): JsonObject {
  // Create a deep copy of the object to avoid modifying the original
  const objectCopy = safeJsonParse(object);

  if (toRemove in objectCopy) {
    delete objectCopy[toRemove as keyof typeof objectCopy];
  }
  return objectCopy;
}

/**
 * Removes multiple keys from a JSON object
 * @param object The object to remove keys from
 * @param keysToRemove Array of keys to remove
 * @returns A new object with the specified keys removed
 */
export function removeKeysFromJson(
  object:
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | CanonicalBusinessEvent,
  keysToRemove: string[]
): JsonObject {
  // Create a deep copy of the object to avoid modifying the original
  const objectCopy = safeJsonParse(object);

  keysToRemove.forEach((key) => {
    if (key in objectCopy) {
      delete objectCopy[key as keyof typeof objectCopy];
    }
  });

  return objectCopy;
}

/**
 * Sorts a JSON object (including nested objects) by its keys.
 * @param obj The JSON to sort
 * @param space Adds indentation, white space, and line break characters to the return-value JSON text to make it easier to read.
 */
export function sortedStringify(obj: any, space?: string | number): string {
  return JSON.stringify(obj, (key, value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return Object.fromEntries(
          Object.entries(value).sort(([a], [b]) => a.localeCompare(b))
      );
    }
    return value;
  }, space);
}
